import BaseApi from './BaseApi.js';

/**
 * Public changelog API client — consumed by the What's New drawer
 * (modules/common/WhatsNewDrawer/useChangelog.js).
 *
 * Admin CRUD lives separately in changelogAdminApi.js; keep the two clients
 * apart.
 *
 * IMPORTANT: endpoints are relative to `apiBaseUrl`, which ALREADY includes
 * `/api/v1` (see api/constants.js). Do NOT prefix `/api/v1` here or the request
 * URL double-prefixes.
 */
class ChangelogApi extends BaseApi {
  /**
   * Fetch published changelog releases for an area. The Symfony backend merges
   * the global `general` area into any requested area.
   *
   * @param {string} [area] - Area filter, e.g. 'calendar'
   * @param {Object} [options]
   * @param {number} [options.limit] - Optional max number of releases
   * @param {string} [options.since] - Optional ISO date lower bound
   * @returns {Promise<{data: Object}>} BaseApi envelope; `data` is the Symfony
   *   payload `{ success, data: { lastSeenReleaseId, lastSeenVersion, releases } }`.
   */
  static async getChangelog(area, { limit, since } = {}) {
    const query = Object.entries({ area, limit, since })
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    const url = query ? `/changelog?${query}` : '/changelog';
    return await this.get(url);
  }

  /**
   * Mark the changelog as seen up to the given release for an area.
   *
   * @param {number|string} releaseId
   * @param {string} [area]
   * @returns {Promise<{data: Object}>}
   */
  static async markAsSeen(releaseId, area) {
    return await this.post('/changelog/seen', { releaseId, area });
  }
}

export default ChangelogApi;
